import type {
  EncounterNoteBlock,
  EncounterNoteDocument,
  EncounterNoteInline,
  EncounterNoteMark,
} from "./encounter-note.types";

/**
 * Minimal TipTap/ProseMirror JSON node shape used by the serializer.
 * Kept local so the domain mapper does not depend on @tiptap packages.
 */
export type TipTapJsonNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: Array<{ type?: string }>;
  content?: TipTapJsonNode[];
};

export class EncounterNoteMapperError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EncounterNoteMapperError";
  }
}

/**
 * Maps TipTap editor JSON to the canonical encounter note document.
 * Rejects unsupported block types; unknown text marks are dropped.
 */
export function mapTipTapJsonToEncounterNoteDocument(
  json: unknown,
): EncounterNoteDocument {
  if (!isRecord(json) || json.type !== "doc") {
    throw new EncounterNoteMapperError(
      `Unsupported document root type: ${String(getType(json))}`,
    );
  }

  const content = Array.isArray(json.content)
    ? json.content.map((node) => mapBlock(node))
    : [];

  return { type: "doc", content };
}

function mapBlock(node: unknown): EncounterNoteBlock {
  if (!isRecord(node) || typeof node.type !== "string") {
    throw new EncounterNoteMapperError("Invalid block node");
  }

  switch (node.type) {
    case "paragraph":
      return {
        type: "paragraph",
        content: mapInlineContent(node.content),
      };
    case "heading": {
      const level = readHeadingLevel(node.attrs);
      return {
        type: "heading",
        attrs: { level },
        content: mapInlineContent(node.content),
      };
    }
    case "bulletList": {
      const items = Array.isArray(node.content) ? node.content : [];
      if (items.length === 0) {
        throw new EncounterNoteMapperError(
          "bulletList must contain list items",
        );
      }
      return {
        type: "bulletList",
        content: items.map(mapListItem),
      };
    }
    case "horizontalRule":
      return { type: "horizontalRule" };
    default:
      throw new EncounterNoteMapperError(
        `Unsupported block type: ${node.type}`,
      );
  }
}

function mapListItem(node: unknown): {
  type: "listItem";
  content: EncounterNoteBlock[];
} {
  if (!isRecord(node) || node.type !== "listItem") {
    throw new EncounterNoteMapperError(
      `Unsupported list child type: ${String(getType(node))}`,
    );
  }

  const content = Array.isArray(node.content)
    ? node.content.map((child) => mapBlock(child))
    : [];

  return { type: "listItem", content };
}

function mapInlineContent(content: unknown): EncounterNoteInline[] | undefined {
  if (!Array.isArray(content) || content.length === 0) {
    return undefined;
  }
  return content.map(mapInline);
}

function mapInline(node: unknown): EncounterNoteInline {
  if (!isRecord(node) || typeof node.type !== "string") {
    throw new EncounterNoteMapperError("Invalid inline node");
  }

  switch (node.type) {
    case "text": {
      if (typeof node.text !== "string") {
        throw new EncounterNoteMapperError("Text node missing text");
      }
      const marks = mapMarks(node.marks);
      return marks?.length
        ? { type: "text", text: node.text, marks }
        : { type: "text", text: node.text };
    }
    case "hardBreak":
      return { type: "hardBreak" };
    default:
      throw new EncounterNoteMapperError(
        `Unsupported inline type: ${node.type}`,
      );
  }
}

function mapMarks(marks: unknown): EncounterNoteMark[] | undefined {
  if (!Array.isArray(marks) || marks.length === 0) {
    return undefined;
  }

  const mapped: EncounterNoteMark[] = [];
  for (const mark of marks) {
    if (!isRecord(mark) || typeof mark.type !== "string") continue;
    if (mark.type === "bold" || mark.type === "italic") {
      mapped.push({ type: mark.type });
    }
  }

  return mapped.length > 0 ? mapped : undefined;
}

function readHeadingLevel(attrs: unknown): 1 | 2 | 3 {
  if (!isRecord(attrs)) {
    throw new EncounterNoteMapperError("Heading missing level");
  }
  const level = attrs.level;
  if (level === 1 || level === 2 || level === 3) {
    return level;
  }
  throw new EncounterNoteMapperError(
    `Unsupported heading level: ${String(level)}`,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function getType(value: unknown): unknown {
  return isRecord(value) ? value.type : undefined;
}
